import { SimpleUserAvatar } from '@/components/UserAvatar'
import SimpleUsername from '@/components/SimpleUsername'
import { TInstalledCommand } from '@/atoms/slash-commands'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { cn } from '@/lib/utils'

export interface SlashCommandListProps {
  items: { command: TInstalledCommand }[]
  command: (payload: { command: TInstalledCommand }) => void
}

export interface SlashCommandListHandle {
  onKeyDown: (args: SuggestionKeyDownProps) => boolean
}

const SlashCommandList = forwardRef<SlashCommandListHandle, SlashCommandListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const selectItem = (index: number) => {
    const item = props.items[index]
    if (item) props.command({ command: item.command })
  }

  const upHandler = () =>
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
  const downHandler = () => setSelectedIndex((selectedIndex + 1) % props.items.length)
  const enterHandler = () => selectItem(selectedIndex)

  useEffect(() => {
    setSelectedIndex(props.items.length ? 0 : -1)
  }, [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: SuggestionKeyDownProps) => {
      if (event.key === 'ArrowUp') {
        upHandler()
        return true
      }
      if (event.key === 'ArrowDown') {
        downHandler()
        return true
      }
      if (event.key === 'Enter' && selectedIndex >= 0) {
        enterHandler()
        return true
      }
      return false
    }
  }))

  if (!props.items.length)
    return (
      <div className="border rounded-lg bg-background z-50 pointer-events-auto shadow-md px-3 py-2 text-sm text-muted-foreground">
        No slash commands available
      </div>
    )

  return (
    <div className="border rounded-lg bg-background z-50 pointer-events-auto flex flex-col max-h-80 overflow-y-auto shadow-md">
      {props.items.map((item, index) => (
        <button
          className={cn(
            'cursor-pointer text-start items-center m-1 p-2 outline-none transition-colors rounded-md',
            selectedIndex === index && 'bg-accent text-accent-foreground'
          )}
          key={`${item.command.pubkey}:${item.command.d}`}
          onClick={() => selectItem(index)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <div className="flex gap-2 w-80 items-center truncate pointer-events-none">
            <SimpleUserAvatar user={item.command.pubkey} />
            <div className="flex-1 w-0 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono font-semibold truncate">/{item.command.command}</span>
                <SimpleUsername
                  user={item.command.pubkey}
                  className="truncate text-muted-foreground text-xs"
                />
              </div>
              {item.command.title && (
                <div className="text-xs text-muted-foreground truncate">{item.command.title}</div>
              )}
            </div>
          </div>
        </button>
      ))}
    </div>
  )
})

SlashCommandList.displayName = 'SlashCommandList'

export default SlashCommandList
